/* eslint-disable react/prop-types */
import React, { Component } from "react";
import PropTypes from "prop-types";

// @material-ui/core
import IconButton from "@material-ui/core/IconButton";
import withStyles from "@material-ui/core/styles/withStyles";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Box from "@material-ui/core/Box";
import Modal from "@material-ui/core/Modal";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";

// core components
import Button from "components/CustomButtons/Button.jsx";
import CustomSelect from "components/CustomSelect/CustomSelect.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";

import { prettyJson } from "../../utils/converters.jsx";
import {
  getStatusTypeHelper,
  getStatusTypePreview
} from "../../services/dashboardDataProvider";

const AVAILABLE_STATUS_TYPES = [
  { text: "Meter", value: "meter" },
  { text: "User", value: "user" },
  { text: "Alarm", value: "alarm" },
  { text: "Device", value: "device" },
  { text: "Report", value: "report" }
];

const AVAILABLE_STATUS_TRIGGER_TYPES = [
  { text: "Data", value: "data" },
  { text: "Schedule", value: "schedule" },
  { text: "Data and Schedule", value: "data/schedule" }
];

const AVAILABLE_REPORT_PERIODS = [
  { text: "Yesterday", value: "yesterday" },
  { text: "Weekly", value: "week" },
  { text: "Monthly", value: "month" }
];

const REPORT_PERIOD_HINTS = [
  "Yesterday: previous calendar day in the device timezone.",
  "Week: trailing 7-day window ending at the current week boundary.",
  "Month: previous calendar month in the device timezone."
];

const styles = {
  deviceTypeContainer: {
    flex: 1,
    overflow: "auto",
    marginBottom: "10px"
  },
  rawTelemetryDataContainer: {
    display: "flex",
    flexDirection: "column",
    overflowX: "scroll"
  },
  jsonContainer: {
    display: "flex",
    flexDirection: "column"
  },
  alarmActions: {
    display: "flex",
    justifyContent: "flex-end"
  },
  alarmModalBox: {
    display: "flex",
    flexDirection: "column",
    padding: "15px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "60%",
    height: "80%",
    overflow: "hidden",
    opacity: "100%",
    zIndex: 2000,
    backgroundColor: "white",
    borderRadius: "10px",
    border: "1px solid rgba(0, 0, 0, 0.3)",
    "-webkit-box-shadow": "0 3px 7px rgba(0, 0, 0, 0.3)",
    "-moz-box-shadow": "0 3px 7px rgba(0, 0, 0, 0.3)",
    boxShadow: "0 3px 7px rgba(0, 0, 0, 0.3)",
    "-webkit-background-clip": "padding-box",
    "-moz-background-clip": "padding-box",
    backgroundClip: "padding-box"
  },
  alarmSelectionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px",
    flexShrink: 0,
    borderTop: "1px solid #e0e0e0"
  },
  previewContainer: {
    marginTop: "10px",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    padding: "10px",
    maxHeight: "260px",
    overflowY: "auto"
  },
  previewRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #f0f0f0",
    padding: "8px 0"
  },
  previewKey: {
    fontFamily: "monospace",
    color: "#1976d2",
    fontWeight: 600
  },
  detailBox: {
    maxHeight: "70vh",
    overflowY: "auto"
  },
  helperSection: {
    marginTop: "12px",
    padding: "12px",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    backgroundColor: "#fafafa"
  },
  helperSectionTitle: {
    fontWeight: 700,
    color: "#1976d2",
    marginBottom: "8px"
  },
  helperList: {
    margin: 0,
    paddingLeft: "18px"
  },
  helperCode: {
    fontFamily: "monospace",
    color: "#0f172a",
    backgroundColor: "#eef2ff",
    padding: "2px 6px",
    borderRadius: "4px"
  },
  helperPre: {
    background: "#f7f7f7",
    padding: 10,
    borderRadius: 4,
    overflowX: "auto"
  }
};

class StatusTypeEditor extends Component {
  constructor(props) {
    super(props);
    const isNewStatusType = !props.statusType || !props.statusType.id;
    this.state = {
      editMode: isNewStatusType,
      schemaInputMode: "text", // "text" or "interactive"
      editingSchemaFields: null,
      previewOpen: false,
      previewLoading: false,
      previewError: "",
      previewData: null,
      detailModalOpen: false,
      selectedDetail: null,
      helperOpen: false,
      helperLoading: false,
      helperError: "",
      helperData: null
    };
  }

  fetchHelper = async () => {
    const { axios, deviceId } = this.props;
    if (!axios) {
      this.setState({
        helperError: "Missing API client for helper data.",
        helperData: null
      });
      return;
    }

    this.setState({
      helperLoading: true,
      helperError: "",
      helperData: null
    });

    try {
      const data = await getStatusTypeHelper(axios, deviceId);
      this.setState({
        helperLoading: false,
        helperData: data
      });
    } catch (error) {
      this.setState({
        helperLoading: false,
        helperError: "Failed to load expression helper."
      });
    }
  };

  openHelper = async () => {
    this.setState({ helperOpen: true });
    await this.fetchHelper();
  };

  fetchPreview = async () => {
    const { axios, deviceId, statusType } = this.props;
    if (!axios || !deviceId || !statusType) {
      this.setState({
        previewError: "Missing device context for preview.",
        previewData: null
      });
      return;
    }

    this.setState({
      previewLoading: true,
      previewError: "",
      previewData: null,
      detailModalOpen: false,
      selectedDetail: null
    });

    try {
      const data = await getStatusTypePreview(axios, deviceId, statusType);
      this.setState({
        previewData: data,
        previewLoading: false
      });
    } catch (error) {
      this.setState({
        previewLoading: false,
        previewError: "Failed to calculate preview values."
      });
    }
  };

  openPreview = async () => {
    this.setState({
      previewOpen: true
    });
    await this.fetchPreview();
  };

  openDetailModal = detail => {
    this.setState({
      detailModalOpen: true,
      selectedDetail: detail
    });
  };

  isValidJson(text) {
    if (typeof text !== "string") {
      return false;
    }
    try {
      JSON.parse(text);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Extract available fields from raw data
  extractAvailableFields = () => {
    const { rawData } = this.props;
    if (!rawData || rawData.length === 0) return [];

    const fields = new Set();

    rawData.forEach(item => {
      if (item.data && typeof item.data === "object") {
        this.extractFieldsFromObject(item.data, "", fields);
      }
    });

    return Array.from(fields).sort();
  };

  extractFieldsFromObject = (obj, prefix, fields) => {
    Object.keys(obj).forEach(key => {
      const fullPath = prefix ? `${prefix}.${key}` : key;

      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        this.extractFieldsFromObject(obj[key], fullPath, fields);
      } else if (
        typeof obj[key] === "number" ||
        typeof obj[key] === "string"
      ) {
        fields.add(fullPath);
      }
    });
  };

  initializeInteractiveSchema = schema => {
    const fields = (schema && schema.fields) || [];
    this.setState({
      editingSchemaFields: JSON.parse(JSON.stringify(fields)),
      schemaInputMode: "interactive"
    });
  };

  addSchemaField = () => {
    const { editingSchemaFields } = this.state;
    const newFields = [
      ...(editingSchemaFields || []),
      {
        target: "",
        type: "raw",
        source: "",
        multiplier: 1,
        offset: 0
      }
    ];
    this.setState({ editingSchemaFields: newFields });
  };

  updateSchemaField = (fieldIndex, fieldName, value) => {
    const { editingSchemaFields } = this.state;
    const newFields = [...editingSchemaFields];
    newFields[fieldIndex] = {
      ...newFields[fieldIndex],
      [fieldName]:
        fieldName === "multiplier" || fieldName === "offset"
          ? parseFloat(value) || 0
          : value
    };
    this.setState({ editingSchemaFields: newFields });
  };

  removeSchemaField = fieldIndex => {
    const { editingSchemaFields } = this.state;
    const newFields = editingSchemaFields.filter((_, idx) => idx !== fieldIndex);
    this.setState({ editingSchemaFields: newFields });
  };

  applyInteractiveSchema = () => {
    const { statusType, onSchemaChange } = this.props;
    const { editingSchemaFields } = this.state;

    const updatedSchema = {
      ...(statusType.translation_schema || {}),
      fields: editingSchemaFields
    };

    if (onSchemaChange) {
      onSchemaChange(updatedSchema);
    }

    return updatedSchema;
  };

  handleUpdate = () => {
    const { onUpdate, enableInteractiveMode } = this.props;
    const { editMode, schemaInputMode } = this.state;
    let updatedSchema;

    if (editMode === true && enableInteractiveMode && schemaInputMode === "interactive") {
      updatedSchema = this.applyInteractiveSchema();
    }

    if (onUpdate) {
      onUpdate(updatedSchema);
    }
  };

  renderInteractiveSchemaBuilder() {
    const { editingSchemaFields } = this.state;
    const availableFields = this.extractAvailableFields();
    const fields = editingSchemaFields || [];

    const fieldTypeOptions = [
      { text: "Raw", value: "raw" },
      { text: "Calculated", value: "calculated" },
      { text: "Data Cache", value: "dataCache" }
    ];

    return (
      <div>
        {fields.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "#999" }}>
            No fields defined. Click &quot;Add Field&quot; to create one.
          </div>
        )}

        {fields.map((field, fieldIdx) => (
          <div
            key={`schema-field-${fieldIdx}`}
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              padding: 10,
              marginBottom: 10,
              backgroundColor: "#fafafa"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8
              }}
            >
              <strong style={{ color: "#1976d2" }}>Field {fieldIdx + 1}</strong>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => this.removeSchemaField(fieldIdx)}
                title="Remove field"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>

            <GridContainer spacing={1}>
              <GridItem xs={12} sm={6} md={6}>
                <CustomInput
                  labelText="Target Name"
                  id={`field-target-${fieldIdx}`}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: field.target || "",
                    onChange: e =>
                      this.updateSchemaField(fieldIdx, "target", e.target.value),
                    placeholder: "e.g., energy_generated"
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={6} md={6}>
                <label style={{ fontSize: 12, color: "#666" }}>
                  Field Type
                </label>
                <CustomSelect
                  labelText={field.type || "raw"}
                  items={fieldTypeOptions}
                  onChange={value =>
                    this.updateSchemaField(fieldIdx, "type", value)
                  }
                />
              </GridItem>

              <GridItem xs={12} sm={12} md={12}>
                <label style={{ fontSize: 12, color: "#666" }}>
                  Source or Expression
                </label>
                {field.type === "raw" && availableFields.length > 0 ? (
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <CustomInput
                        labelText="Source Path"
                        id={`field-source-${fieldIdx}`}
                        formControlProps={{ fullWidth: true }}
                        inputProps={{
                          value: field.source || "",
                          onChange: e =>
                            this.updateSchemaField(
                              fieldIdx,
                              "source",
                              e.target.value
                            ),
                          placeholder: "e.g., meter_0.energy",
                          list: `available-fields-${fieldIdx}`
                        }}
                      />
                      <datalist id={`available-fields-${fieldIdx}`}>
                        {availableFields.map(fieldPath => (
                          <option key={fieldPath} value={fieldPath} />
                        ))}
                      </datalist>
                    </div>
                    <div style={{ paddingTop: 20 }}>
                      <select
                        onChange={e =>
                          this.updateSchemaField(
                            fieldIdx,
                            "source",
                            e.target.value
                          )
                        }
                        value=""
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #ddd",
                          borderRadius: 4,
                          backgroundColor: "white",
                          cursor: "pointer"
                        }}
                      >
                        <option value="">Select from available...</option>
                        {availableFields.map(fieldPath => (
                          <option key={fieldPath} value={fieldPath}>
                            {fieldPath}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <CustomInput
                    labelText={
                      field.type === "calculated"
                        ? "Expression"
                        : field.type === "dataCache"
                          ? "Cache Key"
                          : "Source Path"
                    }
                    id={`field-source-${fieldIdx}`}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: field.source || "",
                      onChange: e =>
                        this.updateSchemaField(
                          fieldIdx,
                          "source",
                          e.target.value
                        ),
                      placeholder:
                        field.type === "calculated"
                          ? "e.g., 230 * meter_1.current or changeToday__meter_0.energy"
                          : field.type === "dataCache"
                            ? "e.g., temperature or load_status"
                            : "e.g., meter_0.energy"
                    }}
                  />
                )}
              </GridItem>

              <GridItem xs={12} sm={6} md={6}>
                <CustomInput
                  labelText="Multiplier"
                  id={`field-multiplier-${fieldIdx}`}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    type: "number",
                    step: "any",
                    value:
                      field.multiplier !== undefined ? field.multiplier : 1,
                    onChange: e =>
                      this.updateSchemaField(
                        fieldIdx,
                        "multiplier",
                        e.target.value
                      )
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={6} md={6}>
                <CustomInput
                  labelText="Offset"
                  id={`field-offset-${fieldIdx}`}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    type: "number",
                    step: "any",
                    value: field.offset !== undefined ? field.offset : 0,
                    onChange: e =>
                      this.updateSchemaField(fieldIdx, "offset", e.target.value)
                  }}
                />
              </GridItem>
            </GridContainer>

            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "#666",
                fontStyle: "italic"
              }}
            >
              Preview: {field.target ? `${field.target} = ` : ""}
              {field.source ? `(${field.source})` : "(no source)"}
              {field.multiplier !== 1 && ` × ${field.multiplier}`}
              {field.offset !== 0 && ` + ${field.offset}`}
            </div>
          </div>
        ))}

        <div style={{ marginTop: 10, marginBottom: 15, textAlign: "left" }}>
          <Button
            color="info"
            simple
            size="sm"
            onClick={this.addSchemaField}
            style={{ marginRight: 10 }}
          >
            <AddIcon style={{ fontSize: 16, marginRight: 4 }} />
            Add Field
          </Button>
          <Button
            color="success"
            simple
            size="sm"
            onClick={() => this.applyInteractiveSchema()}
          >
            Apply Changes
          </Button>
        </div>

        {availableFields.length > 0 && (
          <details style={{ marginTop: 15, fontSize: 12, color: "#666" }}>
            <summary style={{ cursor: "pointer", fontWeight: "bold" }}>
              Available Fields from Raw Data ({availableFields.length})
            </summary>
            <div
              style={{
                marginTop: 8,
                maxHeight: 150,
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
                padding: 8,
                borderRadius: 4
              }}
            >
              {availableFields.map(field => (
                <div
                  key={field}
                  style={{
                    padding: "2px 0",
                    fontFamily: "monospace",
                    fontSize: 11
                  }}
                >
                  • {field}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  }

  render() {
    const {
      classes,
      open,
      statusType,
      onClose,
      onUpdate,
      onDelete,
      onChange,
      onSchemaChange,
      canEdit,
      enableInteractiveMode
    } = this.props;
    const {
      editMode,
      schemaInputMode,
      previewOpen,
      previewLoading,
      previewError,
      previewData,
      detailModalOpen,
      selectedDetail,
      helperOpen,
      helperLoading,
      helperError,
      helperData
    } = this.state;

    return (
      <React.Fragment>
      <Modal
        hideBackdrop
        open={open}
        onClose={onClose}
        aria-labelledby="Status Type Editor"
        aria-describedby="Add/Update status type"
      >
        <Box className={classes.alarmModalBox} sx={{ width: 400 }}>
          <div className={classes.alarmActions}>
            <FormControlLabel
              control={
                <Switch
                  disabled={!canEdit}
                  checked={editMode === true}
                  onChange={() => {
                    this.setState({
                      editMode: !editMode
                    });
                  }}
                />
              }
              label="Edit"
            />
            {canEdit && onDelete && (
              <IconButton
                onClick={() => {
                  if (
                    window.confirm(
                      `Are you sure you want to delete "${statusType.name}"?`
                    )
                  ) {
                    onDelete();
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            <Button
              color="info"
              simple
              size="sm"
              onClick={this.openHelper}
            >
              Expression Help
            </Button>
            <Button
              color="info"
              simple
              size="sm"
              onClick={this.openPreview}
            >
              Preview Values
            </Button>
          </div>
          <div className={classes.deviceTypeContainer}>
            <div>
              <label>Status Type</label>
              <CustomSelect
                labelText={statusType.target_type}
                items={AVAILABLE_STATUS_TYPES}
                disabled={editMode !== true}
                onChange={value => onChange && onChange("target_type", value)}
              />
            </div>
            <div>
              <CustomInput
                labelText="Name"
                id="status_type-name"
                formControlProps={{
                  fullWidth: true
                }}
                inputProps={{
                  defaultValue: statusType.name,
                  disabled: editMode !== true,
                  onChange: e => onChange && onChange("name", e.target.value)
                }}
              />
            </div>
            <div>
              <label>Update Trigger</label>
              <CustomSelect
                labelText={statusType.update_trigger}
                items={AVAILABLE_STATUS_TRIGGER_TYPES}
                disabled={editMode !== true}
                onChange={value =>
                  onChange && onChange("update_trigger", value)
                }
              />
            </div>
            {statusType.target_type === "report" && (
              <div>
                <label>Report Period</label>
                <CustomSelect
                  labelText={statusType.report_period || "Select period"}
                  items={AVAILABLE_REPORT_PERIODS}
                  disabled={editMode !== true}
                  onChange={value => onChange && onChange("report_period", value)}
                />
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: "#64748b"
                  }}
                >
                  {REPORT_PERIOD_HINTS.map(hint => (
                    <div key={hint}>{hint}</div>
                  ))}
                </div>
              </div>
            )}
            <div>
              {editMode !== true ? (
                <div className={classes.rawTelemetryDataContainer}>
                  {prettyJson(statusType.translation_schema, classes)}
                </div>
              ) : (
                <React.Fragment>
                  {enableInteractiveMode && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 10
                      }}
                    >
                      <label style={{ fontWeight: "bold" }}>Schema</label>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={schemaInputMode === "interactive"}
                            onChange={() => {
                              const newMode =
                                schemaInputMode === "text"
                                  ? "interactive"
                                  : "text";
                              if (newMode === "interactive") {
                                this.initializeInteractiveSchema(
                                  statusType.translation_schema
                                );
                              } else {
                                this.applyInteractiveSchema();
                                this.setState({
                                  schemaInputMode: "text",
                                  editingSchemaFields: null
                                });
                              }
                            }}
                            size="small"
                          />
                        }
                        label={
                          schemaInputMode === "interactive"
                            ? "Interactive"
                            : "Text"
                        }
                      />
                    </div>
                  )}
                  {schemaInputMode === "text" || !enableInteractiveMode ? (
                    <CustomInput
                      labelText="Schema JSON"
                      id="status_type-data_schema"
                      formControlProps={{
                        fullWidth: true
                      }}
                      inputProps={{
                        defaultValue: JSON.stringify(
                          statusType.translation_schema
                        ),
                        error: !this.isValidJson(
                          statusType.translation_schema
                        ),
                        disabled: editMode !== true,
                        onChange: e =>
                          onSchemaChange &&
                          onSchemaChange(
                            this.isValidJson(e.target.value)
                              ? JSON.parse(e.target.value)
                              : e.target.value
                          )
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        maxHeight: 400,
                        overflowY: "auto",
                        border: "1px solid #ddd",
                        padding: 10,
                        borderRadius: 4
                      }}
                    >
                      {this.renderInteractiveSchemaBuilder()}
                    </div>
                  )}
                </React.Fragment>
              )}
            </div>
          </div>

          <div className={classes.alarmSelectionContainer}>
            <Button
              color="success"
              simple
              disabled={editMode !== true}
              onClick={this.handleUpdate}
            >
              Update
            </Button>
            <Button
              color="warning"
              simple
              onClick={() => {
                this.setState({
                  editMode: false,
                  schemaInputMode: "text",
                  editingSchemaFields: null
                });
                onClose();
              }}
            >
              Cancel
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal
        open={helperOpen}
        onClose={() =>
          this.setState({
            helperOpen: false,
            helperLoading: false,
            helperError: "",
            helperData: null
          })
        }
        aria-labelledby="Status Expression Helper"
        aria-describedby="Supported status expression helpers and examples"
      >
        <Box className={classes.alarmModalBox} sx={{ width: 860 }}>
          <div className={classes.alarmActions}>
            <strong>Status Expression Helper</strong>
            <Button
              color="info"
              simple
              size="sm"
              disabled={helperLoading}
              onClick={this.fetchHelper}
            >
              Refresh
            </Button>
          </div>
          <div className={classes.detailBox}>
            {helperLoading && <div>Loading helper information...</div>}
            {!helperLoading && helperError && (
              <div style={{ color: "#d32f2f" }}>{helperError}</div>
            )}
            {!helperLoading && !helperError && helperData && (
              <React.Fragment>
                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>
                    {helperData.summary && helperData.summary.title}
                  </div>
                  <div>{helperData.summary && helperData.summary.description}</div>
                  <ul className={classes.helperList}>
                    {((helperData.summary && helperData.summary.expression_rules) || []).map(rule => (
                      <li key={rule}>{rule}</li>
                    ))}
                  </ul>
                </div>

                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>Supported Status Targets</div>
                  <ul className={classes.helperList}>
                    {(helperData.status_targets || []).map(target => (
                      <li key={target.value}>
                        <strong>{target.label}</strong>: {target.description}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>Field Types</div>
                  <ul className={classes.helperList}>
                    {(helperData.field_types || []).map(fieldType => (
                      <li key={fieldType.value}>
                        <strong>{fieldType.value}</strong>: {fieldType.description}
                        <pre className={classes.helperPre}>
                          {JSON.stringify(fieldType.example || {}, null, 2)}
                        </pre>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>Expression Sources</div>
                  <ul className={classes.helperList}>
                    {(helperData.expression_sources || []).map(source => (
                      <li key={source.name}>
                        <strong>{source.name}</strong>: {source.description}
                        <div>
                          Syntax: <span className={classes.helperCode}>{source.syntax}</span>
                        </div>
                        <div>
                          Example: <span className={classes.helperCode}>{source.example}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>Supported Operators</div>
                  <ul className={classes.helperList}>
                    {(helperData.supported_operators || []).map(operator => (
                      <li key={operator.operator}>
                        <span className={classes.helperCode}>{operator.operator}</span>: {operator.description}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>History Snapshots</div>
                  <ul className={classes.helperList}>
                    {(helperData.history_context || []).map(item => (
                      <li key={item.name}>
                        <strong>{item.name}</strong>: {item.description}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>Notes</div>
                  <ul className={classes.helperList}>
                    {(helperData.notes || []).map(note => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </div>

                <div className={classes.helperSection}>
                  <div className={classes.helperSectionTitle}>Device Context</div>
                  <div>
                    Data cache keys: {((helperData.device_context && helperData.device_context.available_data_cache_keys) || []).join(", ") || "None"}
                  </div>
                  <div>
                    Latest raw time: {helperData.device_context && helperData.device_context.latest_raw_time ? String(helperData.device_context.latest_raw_time) : "Unavailable"}
                  </div>
                  <div style={{ marginTop: 8 }}><strong>Available Raw Fields</strong></div>
                  {(helperData.available_raw_fields || []).length > 0 ? (
                    <pre className={classes.helperPre}>
                      {(helperData.available_raw_fields || []).join("\n")}
                    </pre>
                  ) : (
                    <div>No device-specific raw field sample available.</div>
                  )}
                  <div style={{ marginTop: 8 }}><strong>Raw Data Sample</strong></div>
                  <pre className={classes.helperPre}>
                    {JSON.stringify(helperData.raw_data_sample || {}, null, 2)}
                  </pre>
                </div>
              </React.Fragment>
            )}
          </div>
          <div className={classes.alarmSelectionContainer}>
            <Button
              color="warning"
              simple
              onClick={() =>
                this.setState({
                  helperOpen: false,
                  helperLoading: false,
                  helperError: "",
                  helperData: null
                })
              }
            >
              Close
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal
        open={previewOpen}
        onClose={() =>
          this.setState({
            previewOpen: false,
            previewLoading: false,
            previewError: "",
            previewData: null,
            detailModalOpen: false,
            selectedDetail: null
          })
        }
        aria-labelledby="Status Preview"
        aria-describedby="Calculated status preview from latest raw data"
      >
        <Box className={classes.alarmModalBox} sx={{ width: 700 }}>
          <div className={classes.alarmActions}>
            <strong>Status Preview</strong>
            <Button
              color="info"
              simple
              size="sm"
              disabled={previewLoading}
              onClick={this.fetchPreview}
            >
              Refresh
            </Button>
          </div>
          {previewLoading && <div>Calculating from latest device state...</div>}
          {!previewLoading && previewError && (
            <div style={{ color: "#d32f2f" }}>{previewError}</div>
          )}
          {!previewLoading && !previewError && previewData && (
            <div>
              <div className={classes.previewContainer}>
                {(previewData.field_details || []).length === 0 && (
                  <div>No fields available for preview.</div>
                )}
                {(previewData.field_details || []).map(detail => {
                  const key = detail.key;
                  const value = detail.output;
                  return (
                    <div key={key} className={classes.previewRow}>
                      <div>
                        <div className={classes.previewKey}>{key}</div>
                        <div>{value == null ? "null" : String(value)}</div>
                      </div>
                      <Button
                        color="primary"
                        simple
                        size="sm"
                        onClick={() => this.openDetailModal(detail)}
                      >
                        View Input/Output
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className={classes.previewContainer}>
                <div className={classes.previewKey}>Debug Context</div>
                <div style={{ marginTop: 8 }}><strong>First Raw</strong></div>
                <pre style={{ background: "#f7f7f7", padding: 10, borderRadius: 4 }}>
                  {JSON.stringify((previewData.debug_context && previewData.debug_context.first_raw) || {}, null, 2)}
                </pre>
                <div style={{ marginTop: 8 }}><strong>Last Raw</strong></div>
                <pre style={{ background: "#f7f7f7", padding: 10, borderRadius: 4 }}>
                  {JSON.stringify((previewData.debug_context && previewData.debug_context.last_raw) || {}, null, 2)}
                </pre>
                <div style={{ marginTop: 8 }}><strong>First Statuses</strong></div>
                <pre style={{ background: "#f7f7f7", padding: 10, borderRadius: 4 }}>
                  {JSON.stringify((previewData.debug_context && previewData.debug_context.first_statuses) || {}, null, 2)}
                </pre>
                <div style={{ marginTop: 8 }}><strong>Last Statuses</strong></div>
                <pre style={{ background: "#f7f7f7", padding: 10, borderRadius: 4 }}>
                  {JSON.stringify((previewData.debug_context && previewData.debug_context.last_statuses) || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <div className={classes.alarmSelectionContainer}>
            <Button
              color="warning"
              simple
              onClick={() =>
                this.setState({
                  previewOpen: false,
                  previewLoading: false,
                  previewError: "",
                  previewData: null,
                  detailModalOpen: false,
                  selectedDetail: null
                })
              }
            >
              Close
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal
        open={detailModalOpen}
        onClose={() => this.setState({ detailModalOpen: false, selectedDetail: null })}
        aria-labelledby="Field Detail"
        aria-describedby="Input and output detail for selected key"
      >
        <Box className={classes.alarmModalBox} sx={{ width: 760 }}>
          <div className={classes.alarmActions}>
            <strong>Field Input/Output Detail</strong>
          </div>
          <div className={classes.detailBox}>
            <div><strong>Key:</strong> {selectedDetail && selectedDetail.key}</div>
            <div style={{ marginTop: 8 }}><strong>Output:</strong> {selectedDetail ? String(selectedDetail.output) : "-"}</div>
            <div style={{ marginTop: 12 }}><strong>Input:</strong></div>
            <pre style={{ background: "#f7f7f7", padding: 10, borderRadius: 4 }}>
              {JSON.stringify((selectedDetail && selectedDetail.input) || {}, null, 2)}
            </pre>
          </div>
          <div className={classes.alarmSelectionContainer}>
            <Button
              color="warning"
              simple
              onClick={() => this.setState({ detailModalOpen: false, selectedDetail: null })}
            >
              Close
            </Button>
          </div>
        </Box>
      </Modal>
    </React.Fragment>
    );
  }
}

StatusTypeEditor.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  statusType: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSchemaChange: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  enableInteractiveMode: PropTypes.bool,
  rawData: PropTypes.array,
  axios: PropTypes.object,
  deviceId: PropTypes.string
};

StatusTypeEditor.defaultProps = {
  canEdit: true,
  enableInteractiveMode: false,
  rawData: [],
  axios: null,
  deviceId: ""
};

export default withStyles(styles)(StatusTypeEditor);
